import { useEffect, useState } from "react";
import { profiles } from "../lib/api";
import type { Profile, Address, PaymentMethod } from "../lib/api";
import { Plus, Trash2, RefreshCw, User, CreditCard, MapPin, Pencil } from "lucide-react";

type AddressFormData = {
  name: string;
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
};

type PaymentFormData = {
  name: string;
  type: string;
  cardNumber: string;
  cardExpMonth: string;
  cardExpYear: string;
  cardCvv: string;
  cardholderName: string;
};

type ProfileFormData = {
  name: string;
  email: string;
  shippingAddressId: string;
  paymentMethodId: string;
};

const emptyAddressForm: AddressFormData = {
  name: "", firstName: "", lastName: "", addressLine1: "", addressLine2: "",
  city: "", state: "", zipCode: "", phone: "",
};

const emptyPaymentForm: PaymentFormData = {
  name: "", type: "credit_card", cardNumber: "", cardExpMonth: "", cardExpYear: "", cardCvv: "", cardholderName: "",
};

const emptyProfileForm: ProfileFormData = {
  name: "", email: "", shippingAddressId: "", paymentMethodId: "",
};

export default function Profiles() {
  const [profileList, setProfileList] = useState<Profile[]>([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"profiles" | "addresses" | "payments">("profiles");

  // Modal states
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Edit IDs (null = creating new)
  const [editingProfileId, setEditingProfileId] = useState<string | null>(null);
  const [editingAddressId, setEditingAddressId] = useState<string | null>(null);
  const [editingPaymentId, setEditingPaymentId] = useState<string | null>(null);

  // Forms
  const [profileForm, setProfileForm] = useState<ProfileFormData>(emptyProfileForm);
  const [addressForm, setAddressForm] = useState<AddressFormData>(emptyAddressForm);
  const [paymentForm, setPaymentForm] = useState<PaymentFormData>(emptyPaymentForm);

  const fetchData = async () => {
    try {
      const [p, a, pm] = await Promise.all([
        profiles.list(),
        profiles.addresses.list(),
        profiles.paymentMethods.list(),
      ]);
      setProfileList(p);
      setAddresses(a);
      setPaymentMethods(pm);
    } catch (error) {
      console.error("Failed to fetch data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Profile handlers
  const openCreateProfile = () => {
    setEditingProfileId(null);
    setProfileForm(emptyProfileForm);
    setShowProfileModal(true);
  };

  const openEditProfile = (profile: Profile) => {
    setEditingProfileId(profile.id);
    setProfileForm({
      name: profile.name,
      email: profile.email,
      shippingAddressId: profile.shippingAddress.id,
      paymentMethodId: profile.paymentMethod.id,
    });
    setShowProfileModal(true);
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingProfileId) {
        await profiles.update(editingProfileId, profileForm);
      } else {
        await profiles.create(profileForm);
      }
      setShowProfileModal(false);
      setProfileForm(emptyProfileForm);
      setEditingProfileId(null);
      fetchData();
    } catch (error) {
      alert("Failed: " + (error as Error).message);
    }
  };

  // Address handlers
  const openCreateAddress = () => {
    setEditingAddressId(null);
    setAddressForm(emptyAddressForm);
    setShowAddressModal(true);
  };

  const openEditAddress = (addr: Address) => {
    setEditingAddressId(addr.id);
    setAddressForm({
      name: addr.name,
      firstName: addr.firstName,
      lastName: addr.lastName,
      addressLine1: addr.addressLine1,
      addressLine2: addr.addressLine2 || "",
      city: addr.city,
      state: addr.state,
      zipCode: addr.zipCode,
      phone: addr.phone,
    });
    setShowAddressModal(true);
  };

  const handleSaveAddress = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingAddressId) {
        await profiles.addresses.update(editingAddressId, addressForm);
      } else {
        await profiles.addresses.create(addressForm);
      }
      setShowAddressModal(false);
      setAddressForm(emptyAddressForm);
      setEditingAddressId(null);
      fetchData();
    } catch (error) {
      alert("Failed: " + (error as Error).message);
    }
  };

  // Payment handlers
  const openCreatePayment = () => {
    setEditingPaymentId(null);
    setPaymentForm(emptyPaymentForm);
    setShowPaymentModal(true);
  };

  const openEditPayment = (pm: PaymentMethod) => {
    setEditingPaymentId(pm.id);
    setPaymentForm({
      name: pm.name,
      type: pm.type,
      cardNumber: "", // Don't show encrypted value
      cardExpMonth: "",
      cardExpYear: "",
      cardCvv: "",
      cardholderName: "",
    });
    setShowPaymentModal(true);
  };

  const handleSavePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingPaymentId) {
        // Only send non-empty fields for update
        const updateData: Record<string, string> = { name: paymentForm.name, type: paymentForm.type };
        if (paymentForm.cardNumber) updateData.cardNumber = paymentForm.cardNumber;
        if (paymentForm.cardExpMonth) updateData.cardExpMonth = paymentForm.cardExpMonth;
        if (paymentForm.cardExpYear) updateData.cardExpYear = paymentForm.cardExpYear;
        if (paymentForm.cardCvv) updateData.cardCvv = paymentForm.cardCvv;
        if (paymentForm.cardholderName) updateData.cardholderName = paymentForm.cardholderName;
        await profiles.paymentMethods.update(editingPaymentId, updateData);
      } else {
        await profiles.paymentMethods.create(paymentForm);
      }
      setShowPaymentModal(false);
      setPaymentForm(emptyPaymentForm);
      setEditingPaymentId(null);
      fetchData();
    } catch (error) {
      alert("Failed: " + (error as Error).message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="animate-spin text-primary-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-4 border-b border-dark-700">
        {[
          { id: "profiles", label: "Profiles", icon: User },
          { id: "addresses", label: "Addresses", icon: MapPin },
          { id: "payments", label: "Payment Methods", icon: CreditCard },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id as typeof tab)}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
              tab === t.id ? "border-primary-500 text-primary-500" : "border-transparent text-gray-400 hover:text-gray-200"
            }`}
          >
            <t.icon size={18} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Profiles Tab */}
      {tab === "profiles" && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={openCreateProfile} className="btn-primary flex items-center gap-2">
              <Plus size={18} />
              New Profile
            </button>
          </div>
          {profileList.length === 0 ? (
            <div className="card text-center py-12">
              <User size={48} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400">No profiles yet. Create addresses and payment methods first!</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {profileList.map((profile) => (
                <div key={profile.id} className="card">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold">{profile.name}</h4>
                      <p className="text-sm text-gray-400">{profile.email}</p>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => openEditProfile(profile)}
                        className="text-gray-400 hover:text-primary-500 hover:bg-primary-500/20 p-2 rounded"
                        title="Edit"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={async () => {
                          if (confirm("Delete this profile?")) {
                            await profiles.delete(profile.id);
                            fetchData();
                          }
                        }}
                        className="text-error hover:bg-error/20 p-2 rounded"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-dark-600 space-y-2 text-sm">
                    <p className="text-gray-400">
                      <span className="text-gray-500">Address:</span> {profile.shippingAddress.city}, {profile.shippingAddress.state}
                    </p>
                    <p className="text-gray-400">
                      <span className="text-gray-500">Payment:</span> {profile.paymentMethod.name} ({profile.paymentMethod.type})
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Addresses Tab */}
      {tab === "addresses" && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={openCreateAddress} className="btn-primary flex items-center gap-2">
              <Plus size={18} />
              New Address
            </button>
          </div>
          {addresses.length === 0 ? (
            <div className="card text-center py-12">
              <MapPin size={48} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400">No addresses yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {addresses.map((addr) => (
                <div key={addr.id} className="card">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold">{addr.name}</h4>
                      <p className="text-sm text-gray-400">{addr.firstName} {addr.lastName}</p>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => openEditAddress(addr)}
                        className="text-gray-400 hover:text-primary-500 hover:bg-primary-500/20 p-2 rounded"
                        title="Edit"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={async () => {
                          if (confirm("Delete?")) {
                            await profiles.addresses.delete(addr.id);
                            fetchData();
                          }
                        }}
                        className="text-error hover:bg-error/20 p-2 rounded"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-400 mt-2">
                    {addr.addressLine1}<br />
                    {addr.city}, {addr.state} {addr.zipCode}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">{addr.phone}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Payment Methods Tab */}
      {tab === "payments" && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={openCreatePayment} className="btn-primary flex items-center gap-2">
              <Plus size={18} />
              New Payment
            </button>
          </div>
          {paymentMethods.length === 0 ? (
            <div className="card text-center py-12">
              <CreditCard size={48} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400">No payment methods yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {paymentMethods.map((pm) => (
                <div key={pm.id} className="card">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold">{pm.name}</h4>
                      <p className="text-sm text-gray-400">{pm.type}</p>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => openEditPayment(pm)}
                        className="text-gray-400 hover:text-primary-500 hover:bg-primary-500/20 p-2 rounded"
                        title="Edit"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={async () => {
                          if (confirm("Delete?")) {
                            await profiles.paymentMethods.delete(pm.id);
                            fetchData();
                          }
                        }}
                        className="text-error hover:bg-error/20 p-2 rounded"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  {pm.cardNumber && <p className="text-sm text-gray-400 mt-2">{pm.cardNumber}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Profile Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card max-w-md w-full">
            <h3 className="font-display text-lg font-semibold mb-4">
              {editingProfileId ? "Edit Profile" : "Create Profile"}
            </h3>
            <form onSubmit={handleSaveProfile} className="space-y-4">
              <div>
                <label className="label">Profile Name</label>
                <input className="input" value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} required />
              </div>
              <div>
                <label className="label">Email</label>
                <input type="email" className="input" value={profileForm.email} onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })} required />
              </div>
              <div>
                <label className="label">Shipping Address</label>
                <select className="input" value={profileForm.shippingAddressId} onChange={(e) => setProfileForm({ ...profileForm, shippingAddressId: e.target.value })} required>
                  <option value="">Select...</option>
                  {addresses.map((a) => <option key={a.id} value={a.id}>{a.name} - {a.city}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Payment Method</label>
                <select className="input" value={profileForm.paymentMethodId} onChange={(e) => setProfileForm({ ...profileForm, paymentMethodId: e.target.value })} required>
                  <option value="">Select...</option>
                  {paymentMethods.map((pm) => <option key={pm.id} value={pm.id}>{pm.name}</option>)}
                </select>
              </div>
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={() => setShowProfileModal(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">{editingProfileId ? "Save" : "Create"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Address Modal */}
      {showAddressModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="font-display text-lg font-semibold mb-4">
              {editingAddressId ? "Edit Address" : "Add Address"}
            </h3>
            <form onSubmit={handleSaveAddress} className="space-y-4">
              <div><label className="label">Nickname</label><input className="input" value={addressForm.name} onChange={(e) => setAddressForm({ ...addressForm, name: e.target.value })} required /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="label">First Name</label><input className="input" value={addressForm.firstName} onChange={(e) => setAddressForm({ ...addressForm, firstName: e.target.value })} required /></div>
                <div><label className="label">Last Name</label><input className="input" value={addressForm.lastName} onChange={(e) => setAddressForm({ ...addressForm, lastName: e.target.value })} required /></div>
              </div>
              <div><label className="label">Address Line 1</label><input className="input" value={addressForm.addressLine1} onChange={(e) => setAddressForm({ ...addressForm, addressLine1: e.target.value })} required /></div>
              <div><label className="label">Address Line 2</label><input className="input" value={addressForm.addressLine2} onChange={(e) => setAddressForm({ ...addressForm, addressLine2: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-4">
                <div><label className="label">City</label><input className="input" value={addressForm.city} onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })} required /></div>
                <div><label className="label">State</label><input className="input" value={addressForm.state} onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })} required /></div>
                <div><label className="label">ZIP</label><input className="input" value={addressForm.zipCode} onChange={(e) => setAddressForm({ ...addressForm, zipCode: e.target.value })} required /></div>
              </div>
              <div><label className="label">Phone</label><input className="input" value={addressForm.phone} onChange={(e) => setAddressForm({ ...addressForm, phone: e.target.value })} required /></div>
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={() => setShowAddressModal(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">{editingAddressId ? "Save" : "Add"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card max-w-md w-full">
            <h3 className="font-display text-lg font-semibold mb-4">
              {editingPaymentId ? "Edit Payment Method" : "Add Payment Method"}
            </h3>
            <form onSubmit={handleSavePayment} className="space-y-4">
              <div><label className="label">Nickname</label><input className="input" value={paymentForm.name} onChange={(e) => setPaymentForm({ ...paymentForm, name: e.target.value })} required /></div>
              <div><label className="label">Type</label>
                <select className="input" value={paymentForm.type} onChange={(e) => setPaymentForm({ ...paymentForm, type: e.target.value })}>
                  <option value="credit_card">Credit Card</option>
                  <option value="debit_card">Debit Card</option>
                </select>
              </div>
              <div>
                <label className="label">Card Number {editingPaymentId && <span className="text-gray-500">(leave blank to keep current)</span>}</label>
                <input className="input" value={paymentForm.cardNumber} onChange={(e) => setPaymentForm({ ...paymentForm, cardNumber: e.target.value })} required={!editingPaymentId} />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><label className="label">Exp Month</label><input className="input" placeholder="MM" value={paymentForm.cardExpMonth} onChange={(e) => setPaymentForm({ ...paymentForm, cardExpMonth: e.target.value })} required={!editingPaymentId} /></div>
                <div><label className="label">Exp Year</label><input className="input" placeholder="YYYY" value={paymentForm.cardExpYear} onChange={(e) => setPaymentForm({ ...paymentForm, cardExpYear: e.target.value })} required={!editingPaymentId} /></div>
                <div><label className="label">CVV</label><input type="password" className="input" value={paymentForm.cardCvv} onChange={(e) => setPaymentForm({ ...paymentForm, cardCvv: e.target.value })} required={!editingPaymentId} /></div>
              </div>
              <div><label className="label">Cardholder Name</label><input className="input" value={paymentForm.cardholderName} onChange={(e) => setPaymentForm({ ...paymentForm, cardholderName: e.target.value })} required={!editingPaymentId} /></div>
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={() => setShowPaymentModal(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">{editingPaymentId ? "Save" : "Add"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
